import { useEffect, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid, Label, useErrorContext } from '@wf/sw-ui-kit';
import { Input } from '@wf/components';

import Scrollable from '../../../../../components/ListWithContentLayout/components/Scrollable';
import UploadAttachmentsButton from '../UploadAttachmentsButton';
import { type FormType } from '../../Items/types';
import { LicenseField } from '../../LicenseField';
import FilePreview from '../FilePreview';
import FormHeader from '../FormHeader';

import { ArrayFields, AttachedDocumentsTabFields } from '../../../types/fieldEnums';
import { attachedDocumentConfigNames } from '../../../types/configNames';
import { type IAttachmentFormValues } from '../types';

import { filterSelectOptionWithTag } from '../../../../../helpers/filterSelectOptionWithTag';
import { RimmEntityType } from '../../../../rimm/data/rimmEntityEnums';
import { toasterSuccess } from '../../../../../helpers';

import styles from './AttachmentForm.module.scss';

interface IAttachmentForm {
  type: FormType;
  index: number;
  onDelete: () => void;
  onCancel: () => void;
  onSave: () => void;
}

function AttachmentForm({ type, index, onDelete, onCancel, onSave }: IAttachmentForm) {
  const { t } = useTranslation();

  const [viewMode, setViewMode] = useState(type === 'edit');

  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const { triggerWithBackendErrors } = useErrorContext();

  const fileFieldName = `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.fileUrl}`;
  // @ts-expect-error
  const fileErrorMessage = errors[ArrayFields.attachedDocuments]?.[index]?.fileUrl?.message;

  const fileUrl = useWatch({
    name: fileFieldName,
    control,
  });

  const referenceNumber = useWatch({
    name: `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.referenceNumber}`,
    control,
  });

  const id = useWatch({
    name: `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.id}`,
    control,
  });

  const parentAttachmentId = useWatch({
    name: `${ArrayFields.attachedDocuments}.${index}.parentAttachmentId`,
    control,
  });

  useEffect(() => {
    // @ts-expect-error
    if (errors[ArrayFields.attachedDocuments]?.[index]) {
      void triggerWithBackendErrors(`${ArrayFields.attachedDocuments}.${index}`);
    }
  }, []);

  const handleFileChange = (data: IAttachmentFormValues[]) => {
    const attachment = data[0];

    if (attachment?.code) {
      setValue(`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.code}`, attachment.code);
    }

    if (attachment?.referenceNumber) {
      setValue(
        `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.referenceNumber}`,
        attachment?.referenceNumber
      );
    }

    if (attachment?.attachmentDate) {
      setValue(
        `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.attachmentDate}`,
        attachment.attachmentDate
      );
    }

    setValue(
      `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.fileUrl}`,
      attachment?.fileUrl ?? ''
    );
    toasterSuccess({ title: 'File upload successful' });
    isSubmitted && void trigger(`${ArrayFields.attachedDocuments}.${index}`);
  };

  const handleDeleteFile = () => {
    setValue(fileFieldName, '');
    isSubmitted && void trigger(fileFieldName);
  };

  return (
    <div className={styles.container}>
      <FormHeader
        setViewMode={setViewMode}
        viewMode={viewMode}
        onCancel={onCancel}
        onDelete={onDelete}
        onSave={onSave}
        index={index}
        type={type}
      />
      <Scrollable>
        <Grid columns="repeat(3, minmax(0, 1fr)" alignItems="end">
          <LicenseField.RimmSelectField
            label={<Label helpText={t('A unique identifier for the attached document')}>{t('Code')}</Label>}
            name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.code}`}
            configName={attachedDocumentConfigNames[AttachedDocumentsTabFields.code]}
            placeholder={t('Search attachment code')}
            entity={RimmEntityType.attachedDocument}
            filterOption={filterSelectOptionWithTag}
            viewMode={viewMode}
            setValue={setValue}
            withTag
          />

          <LicenseField.TextField
            label={
              <Label helpText={t('A reference number attributed to the attached document')}>
                {t('Reference Number')}
              </Label>
            }
            name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.referenceNumber}`}
            configName={attachedDocumentConfigNames[AttachedDocumentsTabFields.referenceNumber]}
            placeholder={t('Write reference name')}
            viewMode={viewMode}
          />

          <LicenseField.DateField
            label={<Label helpText={t('The date when the attached document was issued or created')}>{t('Date')}</Label>}
            name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.attachmentDate}`}
            configName={attachedDocumentConfigNames[AttachedDocumentsTabFields.referenceNumber]}
            maxDate={new Date().setHours(23, 59, 59, 999)}
            placeholderText={t('Date')}
            viewMode={viewMode}
          />
        </Grid>

        <div className={styles.fileContainer}>
          {fileUrl ? (
            <FilePreview
              parentAttachmentId={parentAttachmentId}
              onReplaceFile={handleDeleteFile}
              fileName={referenceNumber}
              viewMode={viewMode}
              fileURL={fileUrl}
              index={index}
              id={id}
            />
          ) : !viewMode ? (
            <UploadAttachmentsButton
              attachmentCurrentValues={getValues(`${ArrayFields.attachedDocuments}.${index}`)}
              onUploadAttachments={handleFileChange}
              errorMessage={t(fileErrorMessage)}
              isMultiple={false}
            />
          ) : (
            <Grid columns="repeat(2, 1fr)">
              <Input
                errorMessage={fileErrorMessage ? t('File is mandatory') : undefined}
                label={t('Attached File')}
                value="-"
                viewMode
              />
            </Grid>
          )}
        </div>
      </Scrollable>
    </div>
  );
}

export default AttachmentForm;
